import React, { useState, PropsWithChildren } from "react";
//#region types
import { FormControlLayoutProps } from "models/propTypes/genericFormProps";
import { FormGridLayout } from "models/propTypes/basePropTypes";
import { TranslationKeys } from "translations";
import { GridColumnCount } from "models/utilTypes/uiTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { useFormikContext } from "formik";
import useFormContext from "../context/useFormContext";
import AutoGeneratedMessage from "../../messages/autoGeneratedMessage";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const FormControlLayout: React.FC<PropsWithChildren<FormControlLayoutProps>> = (props) => {
  const { names, controlLabel, label, descriptionText } = props;
  const [isAutogenerated, setIsAutogenerated] = useState<boolean>(false);

  const formGridLayout = props.formGridLayout ?? ({ label: 3 } as FormGridLayout);

  const { initialValues, values } = useFormikContext<any>();
  const formContext = useFormContext();

  React.useEffect(() => {
    if (formContext?.autogeneratedValues) {
      const autogeneratedValues = formContext.autogeneratedValues;
      const notAutogenerated = names.filter(
        (name) => !(name in autogeneratedValues) || values[name] !== autogeneratedValues[name]
      );

      if (notAutogenerated.length === 0) !isAutogenerated && setIsAutogenerated(true);
      else isAutogenerated && setIsAutogenerated(false);
    } else {
      setIsAutogenerated(false);
    }
  }, [formContext?.autogeneratedValues, initialValues, values]);

  const getDescriptionText = (horizontal?: boolean) => {
    return (
      descriptionText && (
        <Grid
          item
          className={clsx(cssExports.formControlDescription, horizontal ? "" : cssExports.verticalFormDescription)}
        >
          {typeof descriptionText === "string"
            ? LocalizationHelperInstance.getFormattedMessage(descriptionText, descriptionText)
            : descriptionText}
        </Grid>
      )
    );
  };

  const getControlLabel = () => {
    if (controlLabel && typeof controlLabel !== "string") return controlLabel;

    return controlLabel || label
      ? LocalizationHelperInstance.getFormattedMessage(controlLabel || label, controlLabel || label)
      : "";
  };

  const getAutogeneratedText = () => {
    return (
      isAutogenerated && (
        <Grid container direction="column" justifyContent="center" className={cssExports.autoGeneratedMessageContainer}>
          <AutoGeneratedMessage>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FetchedFromRegister)}
          </AutoGeneratedMessage>
        </Grid>
      )
    );
  };

  const inlineMessageColumnCount = formGridLayout.message === undefined ? 4 : formGridLayout.message;
  const inlineContentColumnCount =
    typeof formGridLayout.label === "number"
      ? ((12 -
          (formGridLayout.label +
            (typeof inlineMessageColumnCount === "number" ? inlineMessageColumnCount : 0))) as GridColumnCount)
      : formGridLayout.label !== true;

  return props.names.length > 0 ? (
    props.inline ? (
      <Grid
        container={!props.contentWidth}
        item
        xs={props.contentWidth}
        spacing={2}
        direction="row"
        className={clsx(cssExports.formControl, props.className)}
      >
        {controlLabel || label ? (
          <Grid
            item
            container={formGridLayout.label === false ? false : true}
            lg={formGridLayout.label}
            direction="column"
            justifyContent={props.verticalAlignLabelCenter && !descriptionText ? "center" : "flex-start"}
            alignItems={props.labelHorizontalAlignment}
            className={cssExports.formControlLabelContainer}
          >
            <Grid item className={cssExports.formControlLabel}>
              {getControlLabel()}
            </Grid>
            {descriptionText && (
              <Grid item className={cssExports.formControlDescription}>
                {getDescriptionText(true)}
              </Grid>
            )}
          </Grid>
        ) : (
          <></>
        )}

        <Grid item container={formGridLayout.label !== true} xs={inlineContentColumnCount}>
          {props.children}
        </Grid>

        <Grid item xs={inlineMessageColumnCount}>
          {getAutogeneratedText()}
        </Grid>
      </Grid>
    ) : (
      <Grid
        container={!props.contentWidth}
        item
        xs={props.contentWidth}
        spacing={!props.contentWidth ? 2 : undefined}
        className={clsx(cssExports.formControl, props.className)}
      >
        <Grid item container xs direction="column" className={cssExports.formControlLabelContainer}>
          {props.enableControlLables ? (
            <Grid item className={clsx(cssExports.formControlLabel, cssExports.formLabelTop)}>
              {getControlLabel()}
            </Grid>
          ) : (
            <></>
          )}
          <Grid item container xs>
            {props.children}
            {getDescriptionText()}
          </Grid>
        </Grid>
        <Grid item xs={formGridLayout.message}>
          {getAutogeneratedText()}
        </Grid>
      </Grid>
    )
  ) : (
    <></>
  );
};

export default FormControlLayout;
